/**
 * CMS Type Guards
 *
 * Type guard functions for runtime type checking of CMS entities.
 * These functions help safely narrow types when working with data from the API.
 */

import {
    Card,
    CMSBanner,
    CMSComponent,
    HeaderComponent,
    NavigationPage,
    NavigationSection,
    Section,
    Title,
} from './entities';

/**
 * Type guard to check if a component is a Banner
 *
 * @param component - The component to check
 * @returns True if the component is a Banner, false otherwise
 *
 * @example
 * ```typescript
 * if (isBanner(component)) {
 *   console.log(component.CTA);  // TypeScript knows this is safe
 * }
 * ```
 */
export function isBanner(component: unknown): component is CMSBanner {
    return (
        component !== null &&
        typeof component === 'object' &&
        '__component' in component &&
        (component as any).__component === 'home.banner'
    );
}

/**
 * Type guard to check if a component is a Title
 *
 * @param component - The component to check
 * @returns True if the component is a Title, false otherwise
 *
 * @example
 * ```typescript
 * if (isTitle(component)) {
 *   console.log(component.Title);  // TypeScript knows this is safe
 * }
 * ```
 */
export function isTitle(component: unknown): component is Title {
    return (
        component !== null &&
        typeof component === 'object' &&
        '__component' in component &&
        (component as any).__component === 'home.title'
    );
}

/**
 * Type guard to check if a component is a Card
 *
 * @param component - The component to check
 * @returns True if the component is a Card, false otherwise
 *
 * @example
 * ```typescript
 * if (isCard(component)) {
 *   console.log(component.TitleText);  // TypeScript knows this is safe
 * }
 * ```
 */
export function isCard(component: unknown): component is Card {
    return (
        component !== null &&
        typeof component === 'object' &&
        'TitleText' in component &&
        'Link' in component &&
        !('Cards' in component) // To distinguish from Section
    );
}

/**
 * Type guard to check if a component is a Section
 *
 * @param component - The component to check
 * @returns True if the component is a Section, false otherwise
 *
 * @example
 * ```typescript
 * if (isSection(component)) {
 *   console.log(component.Name);  // TypeScript knows this is safe
 * }
 * ```
 */
export function isSection(component: unknown): component is Section {
    return (
        component !== null &&
        typeof component === 'object' &&
        'Name' in component &&
        'Cards' in component &&
        Array.isArray((component as any).Cards)
    );
}

/**
 * Type guard to check if a component is a NavigationSection
 *
 * @param component - The component to check
 * @returns True if the component is a NavigationSection, false otherwise
 */
export function isNavigationSection(component: unknown): component is NavigationSection {
    // Navigation sections are like regular sections but may have null Name
    return (
        component !== null &&
        typeof component === 'object' &&
        'Cards' in component &&
        Array.isArray((component as any).Cards)
    );
}

/**
 * Type guard to check if a component is a Header component (Banner or Title)
 *
 * @param component - The component to check
 * @returns True if the component is a Header component, false otherwise
 */
export function isHeaderComponent(component: unknown): component is HeaderComponent {
    return isBanner(component) || isTitle(component);
}

/**
 * Type guard to check if an object is a valid NavigationPage
 *
 * @param data - The data to validate
 * @returns True if the data is a valid NavigationPage
 */
export function isNavigationPage(data: unknown): data is NavigationPage {
    return (
        data !== null &&
        typeof data === 'object' &&
        'Sections' in data &&
        Array.isArray((data as any).Sections)
    );
}

/**
 * Determines the type name of a CMS component
 *
 * @param component - The component to check
 * @returns The component type name as a string
 */
export function getComponentType(component: CMSComponent): string {
    if (isBanner(component)) return 'banner';
    if (isTitle(component)) return 'title';
    if (isCard(component)) return 'card';
    if (isSection(component)) return 'section';

    // This should never happen if all component types are covered
    return 'unknown';
}
